import React, { useState, useEffect } from "react";
import RectangleClassifieds from "../../../creativePreviews/RectangleClassifieds";
import PromotedImagePreview from "../../../creativePreviews/PromotedImagePreview";
import PromotedVideoPreview from "../../../creativePreviews/PromotedVideoPreview";
import HalfVideoPreview from "../../../creativePreviews/HalfVideoPreview";
import { Toast } from "../../../shared/Toast";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { useTableState } from "../utils/useTableState";
import ImageModal from "./ImageModal";
import RotatableImage from "../utils/RotatableImage";
import { proxyDownload } from "../../../utils/proxyDownload";
import API from "../../../services/api";
import { formatTimestamp } from "../../../utils/constants";

const getDiscountStatus = (discount) => {
    if (discount === 0) return { label: 'pending', color: 'bg-yellow-100' };
    if (discount === 1) return { label: 'accepted', color: 'bg-green-100' };
    if (discount === 2) return { label: 'rejected', color: 'bg-red-100' };
    return { label: 'N/A', color: 'bg-gray-100' };
};

const getRejection = (input = "") => {
    const description = input.split("Rejection Reason:")[0].trim() || "";
    const rejection = (input.match(/Rejection Reason: (.*?)(?=\n|$)/g) || []).pop()?.replace("Rejection Reason: ", "").trim() || "";
    return { description, rejection };
};

const getFileType = (url) => {
    const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
    if (["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"].includes(ext)) return "video";
    if (ext === "pdf") return "pdf";
    return "image";
};

const buildCreativeMedia = (data) => {
    const media = [];
    const seen = new Set();
    const add = (url, label) => {
        if (url && !seen.has(url)) { seen.add(url); media.push({ url, type: getFileType(url), label }); }
    };
    if (data?.latest_design) add(data.latest_design, "Latest Design");
    if (data?.video_urls) add(data.video_urls, "Video");
    if (Array.isArray(data?.img_urls?.sub)) {
        data.img_urls.sub.forEach((item, i) => {
            const url = typeof item === "string" ? item : item?.url;
            add(url, `Sub ${i + 1}`);
        });
    }
    return media;
};

const MediaTypeBadge = ({ type }) => (
    <span className={`absolute top-1 left-1 text-[8px] font-bold px-1 rounded ${
        type === "video" ? "bg-red-500 text-white" :
        type === "pdf" ? "bg-orange-500 text-white" :
        "bg-blue-500 text-white"
    }`}>
        {type === "video" ? "VID" : type === "pdf" ? "PDF" : "IMG"}
    </span>
);

const AdDetailsContent = ({ adData }) => {
    const imgUrls = adData.img_urls || {};
    const videoUrl = adData.video_urls ?? null;

    const [isDownloading, setIsDownloading] = useState(false);
    const [mediaUrls, setMediaUrls] = useState([]);
    const [discountData, setDiscountData] = useState({});
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

    const handleMediaPrev = () => setCurrentMediaIndex((p) => (p - 1 + mediaUrls.length) % mediaUrls.length);
    const handleMediaNext = () => setCurrentMediaIndex((p) => (p + 1) % mediaUrls.length);

    const getMedia = async () => {
        const result = await API({
            url: "/alpha_cst_designer/getMedia",
            method: "POST",
            data: { creative_id: adData.creative_id },
        });
        if (result.status === "SUCCESS") {
            const processed = result.data.map((item) => ({
                url: item.img_url,
                type: getFileType(item.img_url),
                label: getFileType(item.img_url) === "video" ? "Video" : "Image",
            }));
            setMediaUrls(processed);
        }
    };

    const getOtherDetails = async () => {
        const result = await API({
            url: "/alpha_cst_designer/getCreativeDetailsDiscount",
            method: "POST",
            data: { creative_id: adData.creative_id },
        });
        if (result.status === "SUCCESS") {
            const data = result.data[0];
            setDiscountData(data);
            const creativeMedia = buildCreativeMedia(data);
            if (creativeMedia.length > 0) {
                setMediaUrls((prev) => {
                    const existingUrls = new Set(prev.map((m) => m.url));
                    const fresh = creativeMedia.filter((m) => !existingUrls.has(m.url));
                    return [...fresh, ...prev];
                });
            }
        }
    };

    useEffect(() => {
        getMedia();
        getOtherDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adData]);

    const handleDownload = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            const files = mediaUrls.map((m) => m.url);
            if (!files.length) {
                if (discountData?.latest_design) files.push(discountData.latest_design);
                if (discountData?.video_urls) files.push(discountData.video_urls);
                if (imgUrls.main) files.push(imgUrls.main);
                if (Array.isArray(imgUrls.sub)) {
                    imgUrls.sub.forEach((obj) => {
                        if (typeof obj === "string") files.push(obj);
                        else if (obj?.url) files.push(obj.url);
                    });
                }
                if (videoUrl) files.push(videoUrl);
            }
            await proxyDownload(files, adData.creative_name || "download");
        } catch {
            Toast("failed", "Download failed.");
        } finally {
            setIsDownloading(false);
        }
    };

    const { modal, setModal, actionHandlers } = useTableState();

    const currentMedia = mediaUrls[currentMediaIndex];

    const previewProps = {
        img_urls: "",
        video_urls: discountData?.video_urls || mediaUrls.find((m) => m.type === "video")?.url || null,
        head_line: adData?.head_line || discountData?.head_line,
        description: adData.description || discountData?.description,
        click_url: adData.click_url || discountData?.click_url,
        button_enabled: 0,
        button_link: adData.click_url || discountData?.click_url,
        name: adData.creative_name,
        position: adData.position,
    };

    const subType = adData.ad_creative_sub_type || discountData?.ad_creative_sub_type;

    return (
        <div className="flex flex-col lg:flex-row p-4 gap-4 overflow-x-hidden">
            {/* Left column: Discount */}
            <div className="text-left w-full lg:w-1/4 flex flex-col gap-3 overflow-y-auto">
                <div className="border rounded-lg p-3 bg-gray-100">
                    <h3 className="text-base font-semibold mb-2">Discount</h3>
                    <div className="space-y-2">
                        <p><span className="font-medium">Actual Value:</span> {+adData.amount}</p>
                        <div>{(discountData?.discounts?.length > 0 ? discountData.discounts : []).map((d, i) => {
                            const isPayLater = d.type === "paylater_level_I" || d.type === "paylater_level_II";
                            const typeLabel = d.type === "paylater_level_I" ? "Pay Later - Level I"
                                : d.type === "paylater_level_II" ? "Pay Later - Level II"
                                : "Discount";
                            return (
                                <div key={i} className={`mt-2 pt-2 ${i > 0 ? "border-2 border-t-black" : ""}`}>
                                    {discountData.discounts.length > 1 && <p className="font-medium text-sm text-gray-500">{typeLabel}</p>}
                                    {isPayLater ? (
                                        <>
                                            <p><span className="font-medium">Pay Later Date:</span> {d.paylater_date ? formatTimestamp(d.paylater_date) : "N/A"}</p>
                                            <p>Approved by - {d.discount_approved_by_name || "N/A"}</p>
                                            <p className="flex flex-col items-start px-1 py-0.5"><span>Request Time</span><span>{d.request_time ? formatTimestamp(d.request_time) : "N/A"}</span></p>
                                            <p className="flex flex-col items-start px-1 py-0.5"><span>Approval Time</span><span>{d.approval_time ? formatTimestamp(d.approval_time) : "N/A"}</span></p>
                                        </>
                                    ) : (
                                        <>
                                            <p><span className="font-medium">Discount:</span> {+d.discount || 0} %</p>
                                            <p><span className="font-medium">Amount after discount:</span> {Math.round(d.discount_amount) || Math.round(+adData.amount * ((+d.discount || 0) / 100))}</p>
                                            <p><span className="font-medium">Order Amount:</span> {d.discount_amount ? Math.round(d.discount_amount * 100 / 118) : Math.round(+adData.amount * 100 / 118)}</p>
                                            <p>Discount Request to - {d.discount_approved_by_name || "N/A"}</p>
                                            <p className={`${getDiscountStatus(d.discount_status).color} rounded-md p-0.5`}>Discount Status - {getDiscountStatus(d.discount_status).label}</p>
                                            <p className="flex flex-col items-start px-1 py-0.5"><span>Request Time</span><span>{d.request_time ? formatTimestamp(d.request_time) : "N/A"}</span></p>
                                            <p className="flex flex-col items-start px-1 py-0.5"><span>Approval Time</span><span>{d.approval_time ? formatTimestamp(d.approval_time) : "N/A"}</span></p>
                                            <p><span className="font-medium">Type:</span> {d.type}</p>
                                            {d.paylater_date && <p><span className="font-medium">Paylater Date:</span> {formatTimestamp(d.paylater_date)}</p>}
                                        </>
                                    )}
                                    {d.rejection_time && <p className="flex flex-col items-start px-1 py-0.5"><span>Rejection Time</span><span>{formatTimestamp(d.rejection_time)}</span></p>}
                                    {d.reject_reason && (
                                        <div className="flex flex-col border rounded-lg p-2 mt-1 bg-red-100">
                                            <p className="font-bold text-red-600 text-sm">Reject Reason:</p>
                                            <p className="text-sm break-words whitespace-pre-wrap">{d.reject_reason}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}</div>
                        {!discountData?.discounts?.length && (
                            <>
                                <p><span className="font-medium">Discount:</span> {+adData.discount || 0} %</p>
                                <p><span className="font-medium">Net Amount:</span> {+adData.discount_amount || +adData.amount}</p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Middle column: Campaign/Dates/Targets */}
            <div className="text-left w-full lg:w-1/4 flex flex-col gap-3 overflow-y-auto">
                <div className="border rounded-lg p-3 bg-gray-100">
                    <h3 className="text-base font-semibold mb-2">Campaign Details</h3>
                    <div className="space-y-1.5">
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Campaign Name</span>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium break-words">{adData.creative_name}</span>
                                {adData.creative_name && (
                                    <button onClick={() => copyToClipboard(adData.creative_name)} className="shrink-0 text-gray-400 hover:text-gray-600 transition" title="Copy Campaign Name">
                                        📋
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Brand</span>
                            <span className="text-sm font-medium break-words">{adData.brand_name || "-"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Client</span>
                            <span className="text-sm font-medium break-words">{adData.client_name || "-"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Ad Type</span>
                            <span className="text-sm font-medium">{adData.ad_type}</span>
                        </div>
                    </div>
                </div>
                <div className="border rounded-lg p-2 bg-gray-100">
                    <h3 className="text-base font-semibold mb-1.5">Dates</h3>
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5 p-1.5 bg-white rounded shadow-sm">
                            <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div className="flex flex-col min-w-0">
                                <span className="text-[10px] text-gray-500 leading-tight">Start</span>
                                <span className="text-xs font-medium text-gray-800 truncate">{formatTimestamp(adData.creative_start_date)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 p-1.5 bg-white rounded shadow-sm">
                            <svg className="w-3.5 h-3.5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div className="flex flex-col min-w-0">
                                <span className="text-[10px] text-gray-500 leading-tight">End</span>
                                <span className="text-xs font-medium text-gray-800 truncate">{formatTimestamp(+adData.creative_start_date + 85800)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 p-1.5 bg-blue-50 rounded shadow-sm border border-blue-100">
                            <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex flex-col min-w-0">
                                <span className="text-[10px] text-blue-600 font-medium leading-tight">Created</span>
                                <span className="text-xs font-semibold text-gray-900 truncate">{formatTimestamp(adData.created_at)}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="border rounded-lg p-3 bg-gray-100">
                    <h3 className="text-base font-semibold mb-2">Sales Person</h3>
                    <div className="space-y-1.5">
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Name</span>
                            <span className="text-sm font-medium">{adData.sales_person?.sales_person_name || "-"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Mobile</span>
                            <span className="text-sm font-medium">{adData.sales_person?.mobile || "-"}</span>
                        </div>
                    </div>
                </div>
                <div className="border rounded-lg p-3 bg-gray-100">
                    <h3 className="text-base font-semibold mb-2">Targets</h3>
                    <div className="space-y-1">
                        {adData?.position && <div className="flex flex-col"><span className="text-xs text-gray-500">Position</span><span className="text-sm font-medium">{adData.position}</span></div>}
                        {discountData?.state_id && <div className="flex flex-col"><span className="text-xs text-gray-500">State</span><span className="text-sm break-words">{discountData?.state_id?.map((t) => t.state_name).join(", ")}</span></div>}
                        {discountData?.dist_id && <div className="flex flex-col"><span className="text-xs text-gray-500">District</span><span className="text-sm break-words">{discountData?.dist_id?.map((t) => t.dist_name).join(", ")}</span></div>}
                        {discountData?.const_id && <div className="flex flex-col"><span className="text-xs text-gray-500">Constituency</span><span className="text-sm break-words">{discountData?.const_id?.map((t) => t.const_name).join(", ")}</span></div>}
                    </div>
                </div>
                <div className="border rounded-lg p-3 bg-gray-100">
                    <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold mb-2">Rough Description</h3>
                        {getRejection(adData?.rough_description)?.description && (
                            <button onClick={() => copyToClipboard(getRejection(adData?.rough_description)?.description)} className="shrink-0 text-gray-400 hover:text-gray-600 transition" title="Copy Description">
                                📋
                            </button>
                        )}
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm break-words">{getRejection(adData?.rough_description)?.description || "No description available"}</p>
                        {getRejection(adData?.rough_description)?.rejection && (
                            <div className="border rounded-lg p-2 bg-red-100">
                                <p className="font-bold text-red-600 text-xs mb-1">Rejection Reason:</p>
                                <p className="text-xs break-words">{getRejection(adData?.rough_description)?.rejection}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Right column: Creative Preview */}
            <div className="w-full lg:w-1/2 border rounded p-4 flex flex-col gap-4 bg-gray-100 min-h-0">
                <div>
                    <h3 className="text-lg font-semibold">Creative Preview</h3>
                    <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-600">
                        <span className="bg-white px-2 py-0.5 rounded border text-xs">
                            <span className="font-medium">Type:</span> {adData.creative_type}
                        </span>
                        {subType && (
                            <span className="bg-white px-2 py-0.5 rounded border text-xs">
                                <span className="font-medium">Format:</span> {subType}
                            </span>
                        )}
                        {(adData.ad_creative_type || discountData?.ad_creative_type) && (
                            <span className={`px-2 py-0.5 rounded border text-xs font-semibold ${discountData?.ad_creative_type === "video" || adData.ad_creative_type === "video" ? "bg-red-100 text-red-700 border-red-200" : "bg-blue-100 text-blue-700 border-blue-200"}`}>
                                {(adData.ad_creative_type || discountData?.ad_creative_type)?.toUpperCase()}
                            </span>
                        )}
                    </div>
                </div>

                {/* Thumbnail strip */}
                {mediaUrls.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto py-1">
                        {mediaUrls.map((item, idx) => (
                            <div
                                key={idx}
                                onClick={() => setCurrentMediaIndex(idx)}
                                className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden cursor-pointer border-2 transition-transform duration-200 ${idx === currentMediaIndex ? "border-blue-500 ring-2 ring-blue-400 scale-105" : "border-gray-300 hover:border-blue-300"}`}
                            >
                                {item.type === "video" ? (
                                    <>
                                        <video src={item.url} className="w-full h-full object-cover" preload="metadata" muted />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                            <svg className="w-5 h-5 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </>
                                ) : item.type === "pdf" ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-orange-50">
                                        <svg className="w-7 h-7 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                                        </svg>
                                    </div>
                                ) : (
                                    <img src={item.url} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                                )}
                                <MediaTypeBadge type={item.type} />
                                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] text-center py-0.5 truncate px-0.5">
                                    {item.label}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Main preview */}
                {mediaUrls.length > 0 && currentMedia ? (
                    <div className="relative">
                        {subType === "classified" && currentMedia.type === "image" ? (
                            <RectangleClassifieds
                                preview={{
                                    cla_brand_caption: adData.cla_brand_caption,
                                    logo_urls: adData.cla_brand_img,
                                    brand_name: adData.cla_brand_name,
                                    btn_text_bgclr: adData.cla_btn_bg_clr,
                                    btn_icon: adData.cla_btn_icon,
                                    button_text: adData.cla_btn_txt,
                                    btn_text_clr: adData.cla_btn_txt_col,
                                    btn_url: adData.cla_btn_url,
                                    no_of_rec: adData.cla_no_of_rec,
                                    sub_type: adData.cla_sub_type,
                                    img_urls: currentMedia.url,
                                    head_line: adData?.head_line || adData.headline,
                                    description: adData.description,
                                    button_enabled: !!adData.cla_btn_txt,
                                }}
                            />
                        ) : subType === "promoted_video_story" || subType === "promoted_image_story" ? (
                            currentMedia.type === "video" ? (
                                <PromotedVideoPreview preview={{ ...previewProps, img_urls: currentMedia.url }} />
                            ) : (
                                <PromotedImagePreview preview={{ ...previewProps, img_urls: currentMedia.url }} />
                            )
                        ) : subType === "half_video" ? (
                            <HalfVideoPreview preview={{ ...previewProps, img_urls: currentMedia.url }} />
                        ) : currentMedia.type === "pdf" ? (
                            <div className="flex flex-col gap-2">
                                <iframe
                                    src={currentMedia.url}
                                    title="PDF Preview"
                                    className="w-full rounded border bg-white"
                                    style={{ height: '420px' }}
                                />
                                <a
                                    href={currentMedia.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-center text-sm text-orange-600 hover:underline"
                                >
                                    Open PDF in new tab ↗
                                </a>
                            </div>
                        ) : currentMedia.type === "image" ? (
                            <div
                                className="cursor-zoom-in"
                                onClick={() => setModal({ open: true, type: "latestDesign", image: currentMedia.url })}
                            >
                                <RotatableImage
                                    src={currentMedia.url}
                                    alt={`creative-${currentMediaIndex}`}
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        ) : (
                            <video
                                src={currentMedia.url}
                                controls
                                className="w-full h-full object-cover rounded"
                            />
                        )}

                        {mediaUrls.length > 1 && (
                            <>
                                <button onClick={handleMediaPrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all z-10" aria-label="Previous">
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <button onClick={handleMediaNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all z-10" aria-label="Next">
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </>
                        )}

                        {/* Current item indicator */}
                        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                            {currentMediaIndex + 1} / {mediaUrls.length} &nbsp;·&nbsp; {currentMedia.label}
                        </div>
                    </div>
                ) : (
                    <div className="font-bold text-lg text-center flex items-center justify-center w-full h-64 border rounded bg-gray-100 text-gray-500">NO MEDIA FOUND</div>
                )}

                <button
                    className={`mx-3 px-2 py-1 rounded-lg shadow-md ${isDownloading ? "bg-green-500 cursor-progress" : "bg-blue-400"}`}
                    onClick={handleDownload}
                    disabled={isDownloading}
                >
                    {isDownloading ? "Downloading…" : `Download ${mediaUrls.length > 1 ? "All as ZIP" : "Preview"}`}
                </button>
            </div>

            <ImageModal
                isOpen={modal?.open && modal?.type === "latestDesign"}
                image={modal?.image}
                alt="preview"
                onClose={actionHandlers.closeModal}
            />
        </div>
    );
};

export default AdDetailsContent;
